/**
 * lnDiscrepancies — what an FSR must notice in a LexisNexis report (Ginger McCullough 2026-11-02).
 *
 * Ginger: "is there a way to highlight those more? ... So the FSR's are aware of the risks and what the Potential
 * Follow-up actions are?" Mirrors the server's LnReportHtmlBuilder.Discrepancies so the officer app and the
 * Identity Verification PDF list the same items: every detail that did not verify, any fraud index Risk reviews
 * (FraudIndexReview settings), red flags, fraud warnings and risk indicators.
 */
import type {
  IdentityProviderExtractRow,
  IdentityProviderFraudWarningRow,
  IdentityProviderRedFlagRow,
  IdentityProviderVerifDetailRow,
} from '@retail-ops/api-client';

type Settings = Record<string, unknown> | undefined;

/** The six indices: setting key, printed name, extract field. Order and names match FraudIndexReview.All. */
const INDICES: ReadonlyArray<{ key: string; name: string; field: keyof IdentityProviderExtractRow }> = [
  { key: 'synthetic', name: 'Synthetic Identity', field: 'riskIndex_Synthetic' },
  { key: 'stolen', name: 'Stolen Identity', field: 'riskIndex_Stolen' },
  { key: 'manipulated', name: 'Manipulated Identity', field: 'riskIndex_Manipulated' },
  { key: 'vulnerableVictim', name: 'Vulnerable Victim', field: 'riskIndex_VulnerableVictim' },
  { key: 'friendlyFraud', name: 'Friendly Fraud', field: 'riskIndex_FriendlyFraud' },
  { key: 'suspiciousActivity', name: 'Suspicious Activity', field: 'riskIndex_SuspiciousActivity' },
];

export interface FraudIndexReviewSettings {
  enabled: boolean;
  threshold: number;
  hold: boolean;
  counted: ReadonlySet<string>; // printed names
}

/** Same keys and defaults as the server (FraudIndexReview.cs): on, above 4, hold, Friendly Fraud + Suspicious Activity. */
export function fraudIndexReview(settings: Settings): FraudIndexReviewSettings {
  const s = settings ?? {};
  const str = (k: string): string => String(s[k] ?? '').trim();
  const enabled = str('lexisnexis.fraudIndex.review.enabled').toLowerCase() !== 'false';
  const n = Number.parseInt(str('lexisnexis.fraudIndex.review.threshold'), 10);
  const threshold = Number.isFinite(n) && n >= 1 && n <= 9 ? n : 4;
  const hold = str('lexisnexis.fraudIndex.review.mode') !== 'flag';
  const keys = (str('lexisnexis.fraudIndex.review.indices') || 'friendlyFraud,suspiciousActivity').split(',').map((k) => k.trim());
  const counted = new Set(INDICES.filter((i) => keys.includes(i.key)).map((i) => i.name));
  return { enabled, threshold, hold, counted: counted.size > 0 ? counted : new Set(['Friendly Fraud', 'Suspicious Activity']) };
}

/** The indices above the review line, highest first, as "Friendly Fraud 7". */
export function indicesOver(extract: IdentityProviderExtractRow, r: FraudIndexReviewSettings): string[] {
  if (!r.enabled) return [];
  return INDICES
    .map((i) => ({ name: i.name, value: extract[i.field] as number | null | undefined }))
    .filter((i) => r.counted.has(i.name) && typeof i.value === 'number' && i.value > r.threshold)
    .sort((a, b) => (b.value as number) - (a.value as number))
    .map((i) => `${i.name} ${i.value}`);
}

function jsonList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : [];
  } catch {
    return [];
  }
}

export function lnDiscrepancies(
  extract: IdentityProviderExtractRow,
  verifDetails: ReadonlyArray<IdentityProviderVerifDetailRow>,
  redFlags: ReadonlyArray<IdentityProviderRedFlagRow>,
  fraudWarnings: ReadonlyArray<IdentityProviderFraudWarningRow>,
  review: FraudIndexReviewSettings,
): string[] {
  const out: string[] = [];
  for (const v of [...verifDetails].filter((r) => r.matchStatus === 'mismatched')) {
    const entered = v.inputValue?.trim() || '—';
    out.push(v.verifiedValue?.trim()
      ? `${v.fieldName} did not verify (entered: ${entered}; LexisNexis has: ${v.verifiedValue.trim()})`
      : `${v.fieldName} did not verify (entered: ${entered})`);
  }
  const over = indicesOver(extract, review);
  if (over.length > 0) out.push(`Fraud index above ${review.threshold}: ${over.join(', ')}`);
  for (const f of redFlags) {
    const alerts = jsonList(f.alertsJson);
    if (alerts.length > 0) out.push(`Red flag, ${f.category}: ${alerts.join('; ')}`);
  }
  for (const w of [...fraudWarnings].sort((a, b) => a.rowOrder - b.rowOrder)) out.push(`Fraud warning (${w.inputField}): ${w.warningText}`);
  for (const s of jsonList(extract.potentialRiskIndicatorsJson)) out.push(`Risk indicator: ${s}`);
  return out;
}

// ── CIP verification (Jessica Beucler 2026-11-02) ────────────────────────────
// Mirrors the server's CipVerification.cs (same LexisNexis code tables, InstantID attribute reference): a green light
// when name, address, date of birth and SSN are verified; otherwise which still need a document.

export type CipResult = 'verified' | 'partial' | 'not-verified' | 'not-checked' | 'not-available';
export interface CipElement { element: string; result: CipResult; detail: string }

const NAS_FIRST = new Set([2, 3, 4, 8, 9, 10, 12]);
const NAS_LAST = new Set([2, 5, 7, 8, 9, 11, 12]);
const NAS_ADDRESS = new Set([3, 5, 6, 8, 10, 11, 12]);
const NAS_SSN = new Set([4, 6, 7, 9, 10, 11, 12]);
const NAS_MEANING = [
  'nothing found for the details entered', 'the SSN entered belongs to a different name and address', 'first and last name matched',
  'first name and address matched', 'first name and SSN matched', 'last name and address matched', 'address and SSN matched',
  'last name and SSN matched', 'first name, last name and address matched', 'first name, last name and SSN matched',
  'first name, address and SSN matched', 'last name, address and SSN matched', 'first name, last name, address and SSN matched',
];
const DOB_MEANING: Record<number, string> = {
  2: 'only the day matches', 3: 'only the month matches', 4: 'only the day and month match', 5: 'only the day and year match',
  6: 'only the year matches', 7: 'only the month and year match',
};

export function cipVerification(extract: IdentityProviderExtractRow, rows: ReadonlyArray<IdentityProviderVerifDetailRow>): CipElement[] {
  const out: CipElement[] = [];
  const byField = new Map<string, string>();
  for (const r of rows) if (r.matchStatus === 'matched' || r.matchStatus === 'mismatched') {
    const k = r.fieldName.trim().toLowerCase();
    if (!byField.has(k)) byField.set(k, r.matchStatus);
  }
  const row = (...names: string[]): string | undefined => names.map((n) => byField.get(n.toLowerCase())).find((s) => s !== undefined);
  const fromRows = (element: string, statuses: Array<string | undefined>): CipElement => {
    const known = statuses.filter((s): s is string => s !== undefined);
    if (known.length === 0) return { element, result: 'not-checked', detail: 'Not checked on this report.' };
    if (known.length === statuses.length && known.every((s) => s === 'matched')) return { element, result: 'verified', detail: 'Verified (report detail).' };
    if (known.some((s) => s === 'matched')) return { element, result: 'partial', detail: 'Only part verified (report detail).' };
    return { element, result: 'not-verified', detail: 'Did not verify (report detail).' };
  };
  const dob = (): CipElement => {
    const level = Number.parseInt(extract.verifSummary_DobMatchLevel ?? '', 10);
    if (!Number.isInteger(level) || level < 0 || level > 8) return { element: 'Date of birth', result: 'not-checked', detail: 'No date-of-birth result on this report.' };
    const code = `LexisNexis date-of-birth match level ${level}`;
    if (level === 8) return { element: 'Date of birth', result: 'verified', detail: `${code}: month, day and year match.` };
    if (level === 0) return { element: 'Date of birth', result: 'not-checked', detail: `${code}: no date of birth found or none submitted.` };
    if (level === 1) return { element: 'Date of birth', result: 'not-verified', detail: `${code}: nothing matches.` };
    return { element: 'Date of birth', result: 'partial', detail: `${code}: ${DOB_MEANING[level]}.` };
  };

  if (byField.size > 0) {
    out.push(fromRows('Name', [row('First Name'), row('Last Name')]));
    out.push(fromRows('Address', [row('Street', 'Address'), row('City'), row('State'), row('ZIP Code', 'Zip')]));
    out.push(fromRows('Date of birth', [row('Date of Birth', 'DOB')]));
    out.push(fromRows('Social Security number', [row('SSN', 'Social Security Number')]));
  } else {
    const nas = Number.parseInt(extract.verifSummary_NameAddrSsn ?? '', 10);
    if (!Number.isInteger(nas) || nas < 0 || nas > 12) {
      for (const e of ['Name', 'Address', 'Social Security number']) out.push({ element: e, result: 'not-checked', detail: 'No Name/Address/SSN result on this report.' });
    } else {
      const code = `LexisNexis Name/Address/SSN code ${nas}`;
      const first = NAS_FIRST.has(nas); const last = NAS_LAST.has(nas);
      out.push(first && last ? { element: 'Name', result: 'verified', detail: `${code}: first and last name matched.` }
        : first || last ? { element: 'Name', result: 'partial', detail: `${code}: only the ${first ? 'first' : 'last'} name matched.` }
        : { element: 'Name', result: 'not-verified', detail: `${code}: ${NAS_MEANING[nas]}.` });
      out.push(NAS_ADDRESS.has(nas) ? { element: 'Address', result: 'verified', detail: `${code}: address matched.` }
        : { element: 'Address', result: 'not-verified', detail: `${code}: ${NAS_MEANING[nas]}.` });
      out.push(NAS_SSN.has(nas) ? { element: 'Social Security number', result: 'verified', detail: `${code}: SSN matched.` }
        : { element: 'Social Security number', result: 'not-verified', detail: `${code}: ${NAS_MEANING[nas]}.` });
    }
    out.splice(2, 0, dob());
  }
  // InstantID reports a driver's license problem as a risk indicator (41, DD, DF, DM, DV); none = verified.
  if (extract.input_LicenseNumber?.trim()) {
    const dl = jsonList(extract.potentialRiskIndicatorsJson).filter((s) => /driver'?s?['’]? ?licen[sc]e|DL/i.test(s));
    out.push(dl.length === 0
      ? { element: 'Driver license number', result: 'verified', detail: "Checked by LexisNexis; no driver's license problem reported." }
      : { element: 'Driver license number', result: 'not-verified', detail: `LexisNexis: ${dl.join('; ')}.` });
  }
  return out;
}

export function cipAllVerified(elements: ReadonlyArray<CipElement>): boolean {
  return elements.every((e) => e.result === 'verified');
}
